import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, FlaskConical, ExternalLink, RotateCcw } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const CRIMSON = "#bf3b4f";

// Mirrors _DEFAULT_RELEASE_NOTES_PROMPT in backend/routes/release_notes.py.
// Shown as the reset target when the admin clears their customisation.
const DEFAULT_RELEASE_NOTES_PROMPT = `You are a professional technical writer for Prydes Support, an Australian NDIS disability support provider. You draft clear, warm, and friendly release notes emails on behalf of the software team. Your audience is office staff and administrators — not developers. Many of them are not technical, so you must write in plain, everyday English that anyone can understand.

GOLDEN RULES — read these carefully:
1. Output ONLY the email body as valid HTML. No markdown fences. No explanatory text outside the HTML.
2. Structure: <h2> subject line, greeting <p>, then grouped sections using <h3> headings ("Bugs Fixed", "Improvements & New Features", "In Progress" if applicable), a short closing <p>, and a sign-off ("Warm regards,<br/>The Prydes Support Software Team").
3. Use <ul>/<li> for issue lists. Use <strong> for issue IDs. Use <p> for prose paragraphs.
4. If a section has no issues, omit it entirely.

WRITING STYLE — this is the most important part:
- Write 2–3 sentences per issue. Never just one.
- Sentence 1: Describe what was going wrong OR what was missing, in plain language a non-technical person can picture.
- Sentence 2: Describe what was done to fix or build it, again in plain language.
- Sentence 3 (optional but encouraged): Describe the concrete day-to-day benefit.
- NEVER use developer jargon. Forbidden words and phrases: React, rendering error, component, endpoint, MongoDB, API, null, undefined, CSS, frontend, backend, refactor, async, schema, payload, regex, boolean, array, string, object. Translate everything into plain English.
- Lead with the user's experience, not the technical fix.
- Keep a warm, reassuring tone. People should feel confident the system is being looked after.

CATEGORISATION:
- "Bugs Fixed" = issues of type Bug that are Resolved.
- "Improvements & New Features" = Feature Requests that are Resolved, plus any completed enhancements.
- "In Progress" = high-priority items still being worked on that users should be aware of.
- Exclude low-priority unresolved items unless they are clearly relevant to users.`;

export function IssueTrackerAiConfig() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [rnPromptExpanded, setRnPromptExpanded] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.issueTrackerAiConfig()
      .then((data) => { setCfg(data); setDefaultPrompt(data.system_prompt || ""); })
      .catch(() => toast.error("Could not load Issue Tracker AI settings"));
  }, []);

  const save = async () => {
    setBusy(true);
    try { setCfg(await api.saveIssueTrackerAiConfig(cfg)); toast.success("Issue Tracker AI settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const setCrit = (key, field, val) =>
    setCfg((c) => ({ ...c, criteria: { ...c.criteria, [key]: { ...c.criteria[key], [field]: val } } }));

  const runTest = async () => {
    if (!cfg.model) return toast.error("Select a model to test");
    setTesting(true); setTestResult(null);
    try {
      const res = await api.testIssueTrackerAiModel(cfg);
      setTestResult(res);
      if (res.parse_error) toast.warning("Model replied but not in the expected format — see raw output");
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  if (!cfg) return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3 flex justify-center">
      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3" data-testid="issue-tracker-ai-config">
      <div className="flex items-center gap-1.5 mb-1">
        <Sparkles className="h-3 w-3 text-slate-400" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Issue Tracker AI</span>
      </div>
      <p className="text-[11px] text-slate-500 mb-4">
        Independently configured from the Approvals AI. Changing settings here has no effect on
        shift approvals. Models are sourced from the master AI registry.
      </p>

      {/* Model selection */}
      <div className="mb-4 rounded-md border border-slate-200 bg-slate-50/70 p-3">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-700">AI Model</h3>
          <button onClick={() => nav("/settings/ai")}
            className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5 transition-colors">
            <ExternalLink className="h-3 w-3" /> Manage models in AI Settings
          </button>
        </div>
        <select
          value={cfg.model || ""}
          onChange={(e) => setCfg((c) => ({ ...c, model: e.target.value }))}
          className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
          data-testid="it-ai-model-select"
        >
          {(cfg.models || []).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          {cfg.model && !(cfg.models || []).some((m) => m.id === cfg.model) && (
            <option value={cfg.model}>{cfg.model}</option>
          )}
        </select>
        <p className="text-[10px] text-slate-400 mt-1">
          To add or remove models, go to <b>Admin Settings &rarr; AI Settings</b>.
        </p>
      </div>

      {/* Triage Prompt */}
      <div className="mb-4 rounded-md border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-700">Triage Prompt</h3>
          <div className="flex gap-3">
            <button onClick={() => setCfg((c) => ({ ...c, system_prompt: defaultPrompt }))}
              className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5 transition-colors"
              title="Reset to default prompt">
              <RotateCcw className="h-3 w-3" /> Reset to default
            </button>
            <button onClick={() => setPromptExpanded((v) => !v)}
              className="text-[10px] hover:underline" style={{ color: CRIMSON }}>
              {promptExpanded ? "Collapse" : "Edit prompt"}
            </button>
          </div>
        </div>
        <p className="text-[10px] text-slate-500 mb-2">
          The system prompt sent to the AI when triaging a new issue. Customise to match your
          organisation's priorities.
        </p>
        {promptExpanded ? (
          <Textarea
            rows={12}
            value={cfg.system_prompt || ""}
            onChange={(e) => setCfg((c) => ({ ...c, system_prompt: e.target.value }))}
            className="text-xs font-mono"
            data-testid="it-ai-system-prompt"
            placeholder="Enter the system prompt for issue triage..."
          />
        ) : (
          <div className="text-[10px] text-slate-400 bg-slate-50 rounded p-2 italic line-clamp-2">
            {(cfg.system_prompt || "").split("\n")[0]}...
          </div>
        )}
      </div>

      {/* Release Notes Prompt */}
      <div className="mb-4 rounded-md border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-700">Release Notes Prompt</h3>
          <div className="flex gap-3">
            <button
              onClick={() => setCfg((c) => ({ ...c, release_notes_prompt: DEFAULT_RELEASE_NOTES_PROMPT }))}
              className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5 transition-colors"
              title="Reset to default prompt"
            >
              <RotateCcw className="h-3 w-3" /> Reset to default
            </button>
            <button
              onClick={() => setRnPromptExpanded((v) => !v)}
              className="text-[10px] hover:underline"
              style={{ color: CRIMSON }}
            >
              {rnPromptExpanded ? "Collapse" : "Edit prompt"}
            </button>
          </div>
        </div>
        <p className="text-[10px] text-slate-500 mb-2">
          The instructions sent to the AI when generating a release notes email from the Issue Tracker.
          Customise the tone, level of detail, or formatting to suit how your team communicates.
        </p>
        {rnPromptExpanded ? (
          <Textarea
            rows={16}
            value={cfg.release_notes_prompt ?? DEFAULT_RELEASE_NOTES_PROMPT}
            onChange={(e) => setCfg((c) => ({ ...c, release_notes_prompt: e.target.value }))}
            className="text-xs font-mono"
            data-testid="it-ai-release-notes-prompt"
            placeholder="Enter the system prompt for release notes generation..."
          />
        ) : (
          <div className="text-[10px] text-slate-400 bg-slate-50 rounded p-2 italic line-clamp-2">
            {((cfg.release_notes_prompt ?? DEFAULT_RELEASE_NOTES_PROMPT) || "").split("\n")[0]}...
          </div>
        )}
      </div>

      {/* Auto-Triage Criteria */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Auto-Triage Criteria</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(cfg.criteria || {}).map(([key, crit]) => (
            <div key={key} className="border border-slate-200 rounded p-3">
              <h4 className="text-xs font-medium text-slate-600 mb-2 uppercase">{crit.label || key.replace(/_/g, " ")}</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox id={`crit-${key}-enabled`} checked={crit.enabled}
                    onCheckedChange={(checked) => setCrit(key, "enabled", checked)} />
                  <Label htmlFor={`crit-${key}-enabled`} className="text-xs text-slate-600">Enabled</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id={`crit-${key}-blocker`} checked={crit.blocker}
                    onCheckedChange={(checked) => setCrit(key, "blocker", checked)} />
                  <Label htmlFor={`crit-${key}-blocker`} className="text-xs text-slate-600">Blocker</Label>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Test */}
      <div className="flex items-center gap-3 mb-4">
        <Button onClick={runTest} disabled={testing || !cfg.model} size="sm" variant="outline" className="flex items-center gap-1">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
          Test Model
        </Button>
        {testResult && (
          <div className="flex-1 bg-slate-50 p-3 rounded text-xs text-slate-700 break-all">
            <strong>Response:</strong> {testResult.response}
            {testResult.parse_error && (
              <div className="mt-1 text-red-500"><strong>Parse Error:</strong> {testResult.parse_error}</div>
            )}
          </div>
        )}
      </div>

      <Button onClick={save} disabled={busy} className="w-full" style={{ backgroundColor: CRIMSON }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}
      </Button>
    </div>
  );
}

